<template>
  <div class="sp-avatar" :class="shapeClass" :style="rootStyle" part="base" :role="ariaRole" :aria-label="label">
    <img
      v-if="showImage"
      :src="image"
      :alt="imageAlt"
      :aria-hidden="contentAriaHidden"
      class="sp-avatar__image"
      :class="imageClass"
      part="image"
      @error="onImageError"
    />
    <sp-icon
      v-else-if="icon"
      :name="icon"
      :fill-color="fillColor"
      aria-hidden="true"
      class="sp-avatar__icon"
      part="icon"
    ></sp-icon>
    <span v-else-if="text" class="sp-avatar__text" part="text" :aria-hidden="contentAriaHidden">{{ text }}</span>
    <slot v-else />
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { toBoolean, toCssUnit } from "#ce/utils/props";
import { props as avatarProps } from "./props.js";

const props = defineProps(avatarProps);

// Retry a fresh URL: clearing the flag when `image` changes lets a row that was
// recycled (virtualised lists) show its new photo instead of staying on the
// previous one's fallback glyph.
const imageFailed = ref(false);
watch(
  () => props.image,
  () => (imageFailed.value = false),
);

function onImageError() {
  imageFailed.value = true;
}

const coverFit = computed(() => toBoolean(props.cover));

const showImage = computed(() => Boolean(props.image) && !imageFailed.value);

const imageClass = computed(() => ({ "--contain": !coverFit.value }));

// When `label` is set the avatar carries its own accessible name (role="img"),
// so its visual content is hidden from assistive tech to avoid a double read.
// Without a label the avatar is decorative: the icon is always aria-hidden and
// the image falls back to its `alt`.
const ariaRole = computed(() => (props.label ? "img" : undefined));
const contentAriaHidden = computed(() => (props.label ? "true" : undefined));
const imageAlt = computed(() => (props.label ? "" : props.alt));

const SHAPES = ["circle", "rounded", "square"];
const shapeClass = computed(() => `--shape-${SHAPES.includes(props.shape) ? props.shape : "circle"}`);

const rootStyle = computed(() => {
  const style = {};
  if (props.size != null && props.size !== "") {
    style["--sp-avatar-size"] = toCssUnit(props.size);
  }
  if (props.color) {
    style["--sp-avatar-background-color"] = props.color;
  }
  return style;
});
</script>

<style>
:host {
  --sp-avatar-size: var(--sp-comp-avatar-size, 2.5rem);
  --sp-avatar-background-color: var(--sp-comp-avatar-background-color, transparent);
  /* Glyph inset: the proportion of the avatar the `icon` glyph occupies. */
  --sp-avatar-icon-ratio: var(--sp-comp-avatar-icon-ratio, 0.6);
  display: inline-block;
}
</style>

<style scoped lang="scss">
.sp-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--sp-avatar-size);
  height: var(--sp-avatar-size);
  overflow: hidden;
  background-color: var(--sp-avatar-background-color);
  // Keep round/rounded avatars from being squashed by a flex parent.
  flex: 0 0 auto;

  &.--shape-circle {
    border-radius: var(--sp-comp-avatar-radius-circle, 50%);
  }

  &.--shape-rounded {
    border-radius: var(--sp-comp-avatar-radius-rounded, var(--sp-sys-shape-control));
  }

  &.--shape-square {
    border-radius: var(--sp-comp-avatar-radius-square, 0);
  }
}

.sp-avatar__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  // Bias raster portraits toward the face, matching the legacy avatar crop.
  object-position: top;

  &.--contain {
    width: calc(var(--sp-avatar-size) * var(--sp-avatar-icon-ratio));
    height: calc(var(--sp-avatar-size) * var(--sp-avatar-icon-ratio));
    object-fit: contain;
    object-position: center;
  }
}

.sp-avatar__icon {
  --size: calc(var(--sp-avatar-size) * var(--sp-avatar-icon-ratio));
}

.sp-avatar__text {
  font-size: calc(var(--sp-avatar-size) * 0.4);
  font-weight: 600;
  line-height: 1;
  color: var(--sp-comp-avatar-text-color, inherit);
  user-select: none;
}
</style>
